import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import {
  Target,
  Eye,
  Heart,
  Users,
  ArrowRight,
  Lightbulb,
  Rocket,
} from "lucide-react";

const TEAM_IMAGE =
  "https://images.unsplash.com/photo-1758873268663-5a362616b5a7?crop=entropy&cs=srgb&fm=jpg&q=85&w=800";

const values = [
  {
    icon: Lightbulb,
    title: "Innovation",
    description:
      "We constantly explore new ways to help small businesses succeed in the digital world.",
  },
  {
    icon: Heart,
    title: "Empathy",
    description:
      "We understand the challenges small businesses face and build solutions that truly help.",
  },
  {
    icon: Users,
    title: "Partnership",
    description:
      "We see ourselves as partners in your growth journey, not just service providers.",
  },
  {
    icon: Rocket,
    title: "Results",
    description:
      "Every solution we build is focused on delivering measurable business outcomes.",
  },
];

const timeline = [
  {
    year: "2024",
    title: "The Beginning",
    description:
      "Causaler was founded with a mission to democratize digital tools for small businesses.",
  },
  {
    year: "2025",
    title: "Reality from Idea",
    description: "From brainstorming journey to real Ideas",
  },
  {
    year: "2026",
    title: "Growing Impact",
    description:
      "Now serving many businesses across gyms, salons, cafes and retail.",
  },
];

const teamMembers = [
  {
    name: "Saurav Kumar",
    role: "Founder & Developer",
    bio: "Building products that help small businesses grow.",
    image: "/team/saurav.jpg",
  },
  {
    name: "Ankit Sharma",
    role: "Marketing Lead",
    bio: "Helping brands reach the right audience effectively.",
    image: "/team/ankit.jpg",
  },
  {
    name: "Priya Verma",
    role: "UI/UX Designer",
    bio: "Designing clean, modern and usable interfaces.",
    image: "/team/priya.jpg",
  },
];

export default function AboutPage() {
  return (
    <div className="pt-20" data-testid="about-page">
      {/* Hero Section */}
      <section
        className="py-20 md:py-32 bg-gradient-subtle"
        data-testid="about-hero"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <span className="inline-block px-4 py-2 bg-orange-100 text-[#F5941E] rounded-full text-sm font-semibold mb-6">
              About Us
            </span>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-[#0F172A] leading-tight mb-6">
              Empowering Local Businesses to{" "}
              <span className="text-gradient">Thrive Digitally</span>
            </h1>
            <p className="text-lg md:text-xl text-slate-600 leading-relaxed">
              Causaler is a technology-first company dedicated to helping small
              and local businesses grow by building their online presence, tools
              and platforms.
            </p>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-20 md:py-32" data-testid="mission-vision">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-8">
            {/* Mission */}
            <div
              className="bg-white p-10 rounded-3xl border border-slate-100 shadow-card hover:shadow-card-hover transition-all duration-300"
              data-testid="mission-card"
            >
              <div className="w-16 h-16 rounded-2xl bg-orange-100 flex items-center justify-center mb-6">
                <Target className="w-8 h-8 text-[#F5941E]" />
              </div>
              <h2 className="font-heading text-2xl md:text-3xl font-bold mb-4">
                Our Mission
              </h2>
              <p className="text-slate-600 text-lg leading-relaxed">
                To provide simple, scalable and affordable digital solutions
                that enable every small business to establish and grow their
                online presence without technical barriers.
              </p>
            </div>

            {/* Vision */}
            <div
              className="bg-white p-10 rounded-3xl border border-slate-100 shadow-card hover:shadow-card-hover transition-all duration-300"
              data-testid="vision-card"
            >
              <div className="w-16 h-16 rounded-2xl bg-blue-100 flex items-center justify-center mb-6">
                <Eye className="w-8 h-8 text-[#1E5E9F]" />
              </div>
              <h2 className="font-heading text-2xl md:text-3xl font-bold mb-4">
                Our Vision
              </h2>
              <p className="text-slate-600 text-lg leading-relaxed">
                A world where every local gym, salon, cafe and shop has access
                to the same powerful digital tools as large enterprises,
                leveling the playing field.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* The Problem We Solve */}
      <section
        className="py-20 md:py-32 bg-[#F8FAFC]"
        data-testid="problem-section"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-[#F5941E] font-semibold text-sm tracking-wider uppercase">
                The Challenge
              </span>
              <h2 className="font-heading text-3xl md:text-4xl font-bold mt-4 mb-6">
                Small Businesses Deserve{" "}
                <span className="text-[#1E5E9F]">Big Solutions</span>
              </h2>
              <div className="space-y-6 text-slate-600 text-lg">
                <p>
                  Small and local businesses are the backbone of our economy,
                  yet they often struggle to compete in the digital age. They
                  face:
                </p>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 rounded-full bg-[#F5941E] mt-2 flex-shrink-0" />
                    <span>Complex and expensive website solutions</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 rounded-full bg-[#F5941E] mt-2 flex-shrink-0" />
                    <span>
                      Difficulty managing online presence across platforms
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 rounded-full bg-[#F5941E] mt-2 flex-shrink-0" />
                    <span>Challenges in hiring flexible, reliable staff</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 rounded-full bg-[#F5941E] mt-2 flex-shrink-0" />
                    <span>
                      Lack of data-driven insights for decision making
                    </span>
                  </li>
                </ul>
                <p className="font-medium text-[#0F172A]">
                  Causaler exists to solve these challenges with tools built
                  specifically for small businesses.
                </p>
              </div>
            </div>
            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-r from-orange-200/30 to-blue-200/30 rounded-3xl blur-2xl" />
              <img
                src={TEAM_IMAGE}
                alt="Diverse team working together"
                className="relative rounded-3xl w-full h-auto shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Our Story Timeline */}
      <section className="py-20 md:py-32" data-testid="story-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-[#F5941E] font-semibold text-sm tracking-wider uppercase">
              Our Journey
            </span>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold mt-4">
              The Causaler Story
            </h2>
          </div>

          <div className="max-w-3xl mx-auto">
            {timeline.map((item, index) => (
              <div
                key={item.year}
                className="flex gap-6 mb-12 last:mb-0"
                data-testid={`timeline-${index}`}
              >
                <div className="flex flex-col items-center">
                  <div className="w-12 h-12 rounded-full bg-[#F5941E] text-white font-heading font-bold flex items-center justify-center text-sm">
                    {item.year}
                  </div>
                  {index < timeline.length - 1 && (
                    <div className="w-0.5 h-full bg-slate-200 mt-4" />
                  )}
                </div>
                <div className="pb-12">
                  <h3 className="font-heading text-xl font-bold mb-2">
                    {item.title}
                  </h3>
                  <p className="text-slate-600">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section
        className="py-20 md:py-32 bg-[#0F172A]"
        data-testid="values-section"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-[#F5941E] font-semibold text-sm tracking-wider uppercase">
              Our Values
            </span>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold mt-4 text-white">
              What Drives Us
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => (
              <div
                key={value.title}
                className="bg-slate-800/50 p-8 rounded-3xl border border-slate-700/50 hover:border-[#F5941E]/30 transition-all duration-300 group"
                data-testid={`value-card-${index}`}
              >
                <div className="w-14 h-14 rounded-2xl bg-[#F5941E]/10 flex items-center justify-center mb-6 group-hover:bg-[#F5941E]/20 transition-colors duration-300">
                  <value.icon className="w-7 h-7 text-[#F5941E]" />
                </div>
                <h3 className="font-heading text-xl font-bold text-white mb-3">
                  {value.title}
                </h3>
                <p className="text-slate-400">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Team Placeholder */}
      {/* <section className="py-20 md:py-32" data-testid="team-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-[#F5941E] font-semibold text-sm tracking-wider uppercase">
              Our Team
            </span>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold mt-4 mb-6">
              Meet the People Behind Causaler
            </h2>
            <p className="text-slate-600 text-lg max-w-2xl mx-auto">
              A passionate team dedicated to empowering small businesses with
              technology.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {teamMembers.map((member, index) => (
              <div
                key={index}
                className="bg-white rounded-3xl overflow-hidden border border-slate-100 shadow-card hover:shadow-card-hover transition-all duration-300"
              >
                <div className="h-64 bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center">
                  {member.image ? (
                    <img
                      src={member.image}
                      alt={member.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <Users className="w-20 h-20 text-slate-300" />
                  )}
                </div>

                <div className="p-6 text-center">
                  <h3 className="font-heading text-xl font-bold mb-1">
                    {member.name}
                  </h3>
                  <p className="text-[#F5941E] font-medium mb-3">
                    {member.role}
                  </p>
                  <p className="text-slate-600 text-sm">{member.bio}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section> */}

      {/* CTA Section */}
      <section
        className="py-20 md:py-32 bg-gradient-brand"
        data-testid="about-cta"
      >
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6">
            Ready to Partner With Us?
          </h2>
          <p className="text-white/90 text-lg md:text-xl mb-10">
            Let&apos;s discuss how Causaler can help your business grow
            digitally.
          </p>
          <Link to="/contact#contact-form">
            <Button
              className="bg-white text-[#F5941E] hover:bg-slate-100 rounded-full px-8 py-6 text-lg font-semibold transition-all duration-300 hover:scale-105"
              data-testid="about-cta-button"
            >
              Get in Touch
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
